<template>
  <div
    v-if="show"
    class="fixed inset-0 z-[2000] flex items-center justify-center bg-slate-950/70 p-3 sm:p-6"
    role="presentation"
    @click.self="close">
    <section
      class="flex max-h-[min(85vh,52rem)] w-full max-w-3xl flex-col overflow-hidden rounded-xl border border-white/20 bg-ui-bg shadow-2xl"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
      @keydown.esc.stop.prevent="close">
      <header class="flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3 sm:px-5">
        <h2 :id="titleId" class="text-lg font-semibold text-cyan-300">{{ title }}</h2>
        <button type="button" class="rounded px-2 py-1 text-slate-400 hover:bg-white/10 hover:text-white" aria-label="Close picker" @click="close">
          ×
        </button>
      </header>

      <div class="border-b border-white/10 p-3 sm:px-5">
        <input
          ref="searchInput"
          type="search"
          autocomplete="off"
          class="ha-builder-control w-full"
          :aria-label="searchLabel"
          :placeholder="placeholder"
          :value="query"
          @input="query = $event.target.value" />
      </div>

      <p
        v-if="selectedValue && !hasSelectedValue"
        class="mx-4 mt-3 rounded border border-amber-500/30 bg-amber-950/30 px-3 py-2 text-sm text-amber-200 sm:mx-5">
        Saved {{ unknownValueLabel }} <code class="font-mono">{{ selectedValue }}</code> is not in the current Home Assistant list and will be kept unless you select another item.
      </p>

      <div class="ha-picker-results min-h-0 flex-1 overflow-y-auto p-3 sm:p-5" aria-live="polite">
        <p v-if="loading" class="py-10 text-center text-sm text-slate-400">Loading Home Assistant actions…</p>
        <p v-else-if="unavailableMessage" class="py-10 text-center text-sm text-amber-300">{{ unavailableMessage }}</p>
        <p v-else-if="items.length === 0" class="py-10 text-center text-sm text-slate-400">No Home Assistant actions are available.</p>
        <p v-else-if="filteredGroups.length === 0" class="py-10 text-center text-sm text-slate-400">No matching actions.</p>
        <div v-else class="space-y-5">
          <section v-for="group in filteredGroups" :key="group.domain" :aria-label="`${group.domain} actions`">
            <h3 class="mb-2 border-b border-white/10 pb-1 text-xs font-semibold uppercase tracking-wider text-cyan-300">
              {{ group.domain }}
            </h3>
            <div class="space-y-1">
              <button
                v-for="item in group.items"
                :key="item.value"
                type="button"
                role="option"
                :aria-selected="item.value === selectedValue"
                :data-picker-value="item.value"
                class="block w-full rounded border border-transparent px-3 py-2 text-left transition-colors focus:outline-none focus:ring-1 focus:ring-cyan-500"
                :class="item.value === selectedValue ? 'border-cyan-500/50 bg-cyan-900/40' : 'hover:bg-cyan-900/30 focus:bg-cyan-900/30'"
                @click="select(item.value)">
                <span v-if="item.name || item.description" class="block text-sm font-medium text-slate-100">
                  {{ item.name || item.value }}
                </span>
                <span class="block break-all font-mono text-xs text-cyan-200">{{ item.value }}</span>
                <span v-if="item.description" class="mt-1 block whitespace-pre-line text-xs text-slate-400">
                  {{ item.description }}
                </span>
              </button>
            </div>
          </section>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, useId, watch } from 'vue';
import { filterAndGroupPickerItems, selectPickerItem } from './searchablePicker.js';

const props = defineProps({
  show: { type: Boolean, default: false },
  title: { type: String, default: 'Select an item' },
  searchLabel: { type: String, default: 'Search' },
  placeholder: { type: String, default: 'Search…' },
  items: { type: Array, default: () => [] },
  selectedValue: { type: String, default: '' },
  unknownValueLabel: { type: String, default: 'value' },
  loading: { type: Boolean, default: false },
  unavailableMessage: { type: String, default: '' },
});
const emit = defineEmits(['update:show', 'select']);

const query = ref('');
const searchInput = ref(null);
const titleId = useId();

const hasSelectedValue = computed(() => props.items.some(item => item?.value === props.selectedValue));

const filteredGroups = computed(() => filterAndGroupPickerItems(props.items, query.value));

watch(() => props.show, async (show) => {
  if (!show) return;
  query.value = '';
  await nextTick();
  searchInput.value?.focus?.();
});

function close() {
  emit('update:show', false);
}

function select(value) {
  selectPickerItem(value, emit);
}
</script>

<style scoped>
.ha-picker-results {
  scrollbar-color: rgb(71 85 105) transparent;
  scrollbar-width: thin;
}

.ha-picker-results::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.ha-picker-results::-webkit-scrollbar-track {
  background: transparent;
}

.ha-picker-results::-webkit-scrollbar-thumb {
  border: 2px solid transparent;
  border-radius: 9999px;
  background: rgb(71 85 105);
  background-clip: content-box;
}
</style>
